import { ReactNode, useEffect, useRef } from 'react';

let openModalCount = 0;
let originalBodyOverflow: string | null = null;

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  zIndex?: number;
  headerSticky?: boolean;
  closeButtonDisabled?: boolean;
  titleSize?: 'lg' | 'xl';
  titleFont?: 'standard' | 'heading';
  closeOnEscape?: boolean;
  closeOnBackdropClick?: boolean;
}

function Modal({
  title,
  onClose,
  children,
  maxWidth = 'md',
  zIndex = 50,
  headerSticky = false,
  closeButtonDisabled = false,
  titleSize = 'xl',
  titleFont = 'heading',
  closeOnEscape = true,
  closeOnBackdropClick = true,
}: ModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
  };

  const headerClasses = headerSticky
    ? 'flex items-center justify-center relative border-b border-gray-200 py-4 px-6 sticky top-0 bg-white z-10'
    : 'flex items-center justify-center relative border-b border-gray-200 py-4 px-6';

  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !closeButtonDisabled) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, closeButtonDisabled, closeOnEscape]);

  useEffect(() => {
    openModalCount++;

    if (openModalCount === 1) {
      originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      openModalCount = Math.max(0, openModalCount - 1);

      if (openModalCount === 0) {
        document.body.style.overflow = originalBodyOverflow ?? '';
        originalBodyOverflow = null;
      }
    };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      closeOnBackdropClick &&
      !closeButtonDisabled &&
      modalContentRef.current &&
      !modalContentRef.current.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      closeOnBackdropClick &&
      !closeButtonDisabled &&
      (e.key === 'Enter' || e.key === ' ') &&
      modalContentRef.current &&
      !modalContentRef.current.contains(e.target as Node)
    ) {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ zIndex }}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role={closeOnBackdropClick ? 'button' : undefined}
      tabIndex={closeOnBackdropClick ? -1 : undefined}
      aria-label={closeOnBackdropClick ? 'Close modal' : undefined}
    >
      <div
        ref={modalContentRef}
        className={`w-full rounded-lg bg-white shadow-xl ${maxWidthClasses[maxWidth]} ${
          headerSticky ? 'max-h-[90vh] overflow-y-auto' : ''
        }`}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the backdrop handles closing; a native <dialog> would duplicate it
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className={headerClasses}>
          <button
            onClick={onClose}
            disabled={closeButtonDisabled}
            className="absolute left-6 text-gray-600 transition-colors hover:text-gray-800 disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h2
            id="modal-title"
            className={`${titleFont === 'heading' ? 'font-heading ' : ''}${titleSize === 'lg' ? 'text-2xl' : 'text-xl'} font-bold text-gray-900`}
          >
            {title}
          </h2>
        </div>

        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export { Modal };
